import React, { useState, useEffect, useCallback } from 'react';
import {
  configService,
  CancellationPolicyRead,
} from '../../../services/configService';

const CancellationPoliciesCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [policies, setPolicies] = useState<CancellationPolicyRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getStoreId = (): number | undefined => {
    try {
      const userData = localStorage.getItem('managerUserData');
      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.store_id;
      }
    } catch {
      return undefined;
    }
    return undefined;
  };

  const storeId = getStoreId();

  const loadPolicies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await configService.getCancellationPolicies(storeId);
      setPolicies(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cancellation policies');
      console.error('Failed to load cancellation policies:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const formatModificationFee = (feeType: string, feeValue: number): string => {
    if (feeValue === 0) return 'None';
    if (feeType === 'percentage') return `${feeValue}%`;
    return `$${feeValue.toFixed(2)}`;
  };

  const formatRuleDescription = (rule: CancellationPolicyRead['rules'][0]): string => {
    const maxLabel = rule.days_before_checkin_max != null
      ? `${rule.days_before_checkin_min}–${rule.days_before_checkin_max} days`
      : `${rule.days_before_checkin_min}+ days`;

    const feeLabel = rule.fee_type === 'percentage'
      ? (rule.fee_value === 0 ? 'Full refund' : rule.fee_value === 100 ? 'No refund' : `${rule.fee_value}% fee`)
      : `$${rule.fee_value.toFixed(2)} fee`;

    return `${maxLabel}: ${feeLabel}`;
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">C</span>
          <h2>Cancellation Policies</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/cancellation_policies</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            {String.fromCharCode(9660)}
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="loading-state">Loading cancellation policies...</div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button className="btn btn-primary btn-sm" onClick={loadPolicies}>
                Retry
              </button>
            </div>
          ) : policies.length === 0 ? (
            <div className="empty-state">
              <p>No cancellation policies configured. Contact your administrator.</p>
            </div>
          ) : (
            <>
              {policies.map(policy => (
                <div key={policy.id} className="config-item" style={{ marginBottom: '12px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <strong style={{ fontSize: '0.95rem' }}>{policy.name}</strong>
                      {policy.description && (
                        <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: '#6b7280' }}>{policy.description}</p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                      {policy.modification_fee_value > 0 && (
                        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          Mod. Fee: {formatModificationFee(policy.modification_fee_type, policy.modification_fee_value)}
                        </span>
                      )}
                      {policy.no_refund_after_date && (
                        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          No refund after: {policy.no_refund_after_date}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {policy.rules.map((rule, i) => (
                      <span
                        key={i}
                        style={{
                          padding: '3px 8px',
                          background: rule.fee_value === 0 ? '#dcfce7' : rule.fee_value === 100 && rule.fee_type === 'percentage' ? '#fee2e2' : '#fef3c7',
                          color: rule.fee_value === 0 ? '#166534' : rule.fee_value === 100 && rule.fee_type === 'percentage' ? '#dc2626' : '#92400e',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                        }}
                      >
                        {rule.description || formatRuleDescription(rule)}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CancellationPoliciesCard;
